// Field renderers, one per FIELD kind. The wizard maps a field's `kind` to one
// of these. Add a kind = add a case here + the constant in eventTypes/fieldKinds.

import { useRef, useState } from "react";
import { FIELD } from "../../eventTypes/fieldKinds.js";
import { removeHeroImage, urlFor, uploadHeroImage } from "../../firebase/storage.js";
import {
  COMMON_EVENT_REGISTRATION_FIELDS,
  EVENT_REGISTRATION_FIELD_TYPES,
  createRegistrationField,
  normalizeRegistrationFields,
} from "../registration/formConfig.js";

function Labeled({ label, required, children, onFocus }) {
  return (
    <div className="stack" style={{ gap: 6 }} onFocusCapture={onFocus}>
      <span className="muted" style={{ fontSize: ".9rem" }}>
        {label} {required && <span style={{ color: "var(--app-accent)" }}>*</span>}
      </span>
      {children}
    </div>
  );
}

function ChipsField({ value = [], onChange, placeholder }) {
  const [draft, setDraft] = useState("");
  function add() {
    const v = draft.trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setDraft("");
  }
  return (
    <div className="stack" style={{ gap: 8 }}>
      <input
        className="input"
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); add(); }
        }}
      />
      <div className="chips">
        {value.map((t, i) => (
          <span className="chip" key={i} onClick={() => onChange(value.filter((_, j) => j !== i))}
            style={{ cursor: "pointer" }} title="Remove">
            {t} ✕
          </span>
        ))}
      </div>
    </div>
  );
}

function SpeakersField({ value = [], onChange }) {
  function update(i, patch) {
    onChange(value.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  }
  return (
    <div className="stack">
      {value.map((s, i) => (
        <div className="card stack repeat-card" key={i}>
          <div className="row">
            <strong>{s.name || `Speaker ${i + 1}`}</strong>
            <div className="spacer" />
            <button className="btn-ghost btn mini" onClick={() => onChange(value.filter((_, j) => j !== i))}>✕</button>
          </div>
          <input className="input" placeholder="Name" value={s.name || ""}
            onChange={(e) => update(i, { name: e.target.value })} />
          <input className="input" placeholder="Title" value={s.title || ""}
            onChange={(e) => update(i, { title: e.target.value })} />
          <input className="input" placeholder="Org" value={s.org || ""}
            onChange={(e) => update(i, { org: e.target.value })} />
        </div>
      ))}
      <button className="btn-ghost btn" onClick={() => onChange([...value, { name: "" }])}>
        + Add speaker
      </button>
    </div>
  );
}

function ContactsField({ value = [], onChange }) {
  const rows = Array.isArray(value) ? value : [];
  function update(i, patch) {
    onChange(rows.map((contact, j) => (j === i ? { ...contact, ...patch } : contact)));
  }
  return (
    <div className="stack">
      {rows.map((contact, i) => (
        <div className="card stack repeat-card" key={i}>
          <div className="row">
            <strong>{contact.name || `Contact ${i + 1}`}</strong>
            <div className="spacer" />
            <button type="button" className="btn-ghost btn mini" onClick={() => onChange(rows.filter((_, j) => j !== i))}>✕</button>
          </div>
          <input className="input" placeholder="Name" value={contact.name || ""} onChange={(e) => update(i, { name: e.target.value })} />
          <input className="input" placeholder="Designation" value={contact.designation || ""} onChange={(e) => update(i, { designation: e.target.value })} />
          <input className="input" placeholder="Mobile number" value={contact.mobile || ""} onChange={(e) => update(i, { mobile: e.target.value })} />
          <input className="input" type="email" placeholder="Email" value={contact.email || ""} onChange={(e) => update(i, { email: e.target.value })} />
        </div>
      ))}
      <button type="button" className="btn-ghost btn" onClick={() => onChange([...rows, { name: "", designation: "", mobile: "", email: "" }])}>
        + Add contact person
      </button>
    </div>
  );
}

function ToggleField({ value, onChange, description }) {
  return (
    <button
      type="button"
      className={`toggle-field ${value ? "on" : ""}`}
      onClick={() => onChange(!value)}
      aria-pressed={!!value}
    >
      <span className="toggle-track"><span /></span>
      <span>
        <strong>{value ? "Enabled" : "Disabled"}</strong>
        {description && <small>{description}</small>}
      </span>
    </button>
  );
}

function RegistrationFieldsField({ value = [], onChange }) {
  const rows = normalizeRegistrationFields(value);

  function update(index, patch) {
    const next = rows.map((row, rowIndex) => {
      if (rowIndex !== index) return row;
      const updated = { ...row, ...patch };
      if (patch.label !== undefined && patch.id === undefined) {
        updated.id = createRegistrationField({ label: patch.label }).id;
      }
      return updated;
    });
    onChange(normalizeRegistrationFields(next));
  }

  function useCommonFields() {
    onChange(COMMON_EVENT_REGISTRATION_FIELDS.map((field) => ({ ...field })));
  }

  return (
    <div className="stack">
      <div className="card stack repeat-card">
        <div className="row">
          <strong>Registration form fields</strong>
          <div className="spacer" />
          <button type="button" className="btn-ghost btn mini" onClick={useCommonFields}>
            Reset defaults
          </button>
        </div>
        <p className="muted" style={{ margin: 0, fontSize: ".86rem" }}>
          Starts with common fields and stays private to the event creator in the backend.
        </p>
      </div>
      {rows.map((field, index) => (
        <div className="card stack repeat-card" key={field.id || index}>
          <div className="row">
            <strong>{field.label || `Field ${index + 1}`}</strong>
            <div className="spacer" />
            <button type="button" className="btn-ghost btn mini" onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}>✕</button>
          </div>
          <input
            className="input"
            placeholder="Field label"
            value={field.label || ""}
            onChange={(e) => update(index, { label: e.target.value })}
          />
          <div className="field-grid two">
            <select
              className="input"
              value={field.kind || "text"}
              onChange={(e) => update(index, { kind: e.target.value })}
            >
              {EVENT_REGISTRATION_FIELD_TYPES.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <button
              type="button"
              className={`toggle-field ${field.required ? "on" : ""}`}
              onClick={() => update(index, { required: !field.required })}
              aria-pressed={!!field.required}
            >
              <span className="toggle-track"><span /></span>
              <span>
                <strong>{field.required ? "Required" : "Optional"}</strong>
                <small>Control whether attendees must fill this before submitting.</small>
              </span>
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="btn-ghost btn"
        onClick={() => onChange([...rows, createRegistrationField({ id: `custom-${rows.length + 1}`, label: `Custom field ${rows.length + 1}` })])}
      >
        + Add field
      </button>
    </div>
  );
}

function CustomSlidesField({ value = [], onChange, slug }) {
  const rows = Array.isArray(value) ? value : [];
  const [busyIndex, setBusyIndex] = useState(null);

  function update(i, patch) {
    onChange(rows.map((slide, j) => (j === i ? { ...slide, ...patch } : slide)));
  }

  async function onFiles(i, e) {
    const file = e.target.files?.[0];
    if (!file || !slug) return;
    setBusyIndex(i);
    try {
      if (rows[i]?.image) {
        try { await removeHeroImage(rows[i].image); } catch { /* best effort */ }
      }
      const { path } = await uploadHeroImage(slug, file);
      update(i, { image: path });
    } finally {
      setBusyIndex(null);
      e.target.value = "";
    }
  }

  async function removeImage(i) {
    if (rows[i]?.image) {
      try { await removeHeroImage(rows[i].image); } catch { /* best effort */ }
    }
    update(i, { image: "" });
  }

  return (
    <div className="stack">
      {rows.map((slide, i) => (
        <div className="card stack repeat-card custom-slide-editor" key={i}>
          <div className="row">
            <strong>{slide.title || `Slide ${i + 1}`}</strong>
            <div className="spacer" />
            <button type="button" className="btn-ghost btn mini" onClick={() => onChange(rows.filter((_, j) => j !== i))}>✕</button>
          </div>
          <input className="input" placeholder="Slide title" value={slide.title || ""} onChange={(e) => update(i, { title: e.target.value })} />
          <textarea className="input" rows={5} placeholder="Body" value={slide.body || ""} onChange={(e) => update(i, { body: e.target.value })} />
          <div className="stack" style={{ gap: 8 }}>
            <input type="file" accept="image/*" disabled={busyIndex === i} onChange={(e) => onFiles(i, e)} />
            {busyIndex === i && <p className="muted" style={{ margin: 0 }}>Uploading…</p>}
            {slide.image && (
              <div className="hero-editor-item">
                <img src={urlFor(slide.image)} alt="" />
                <button type="button" className="btn-ghost btn" onClick={() => removeImage(i)}>Remove image</button>
              </div>
            )}
          </div>
        </div>
      ))}
      <button type="button" className="btn-ghost btn" onClick={() => onChange([...rows, { title: "", body: "", image: "" }])}>
        + Add slide
      </button>
    </div>
  );
}

function HeroImageField({ value = "", onChange, slug }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  async function onFiles(e) {
    const file = e.target.files?.[0];
    if (!file || !slug) return;
    setBusy(true);
    try {
      if (value) {
        try { await removeHeroImage(value); } catch { /* best effort */ }
      }
      const { path } = await uploadHeroImage(slug, file);
      onChange(path);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function remove() {
    if (value) {
      try { await removeHeroImage(value); } catch { /* best effort */ }
    }
    onChange("");
  }

  return (
    <div className="stack" style={{ gap: 10 }}>
      <input ref={inputRef} type="file" accept="image/*" disabled={busy} onChange={onFiles} />
      <p className="muted" style={{ fontSize: ".82rem", margin: 0 }}>One wide, high-quality image works best.</p>
      {busy && <p className="muted" style={{ margin: 0 }}>Uploading…</p>}
      {value && (
        <div className="hero-editor-item">
          <img src={urlFor(value)} alt="" />
          <button type="button" className="btn-ghost btn" onClick={remove}>Remove image</button>
        </div>
      )}
    </div>
  );
}

function ScheduleField({ value = [], onChange, label = "item", speakers = [] }) {
  const rows = Array.isArray(value) ? value : [];
  function update(i, patch) {
    onChange(rows.map((row, j) => (j === i ? { ...row, ...patch } : row)));
  }
  function add() {
    onChange([...rows, { serial: String(rows.length + 1), topic: "", description: "", speaker: "", speakerInfo: "", day: "", exactDate: "", startTime: "", endTime: "", duration: "" }]);
  }
  return (
    <div className="stack">
      <p className="muted" style={{ fontSize: ".82rem", margin: 0 }}>Autosaved as you type. Add another row only when this one is ready.</p>
      {rows.map((row, i) => (
        <div className="card stack repeat-card" key={i}>
          <div className="row">
            <div>
              <strong>{row.topic || `${label} ${i + 1}`}</strong>
              <div className="muted" style={{ fontSize: ".78rem" }}>{[row.day, row.exactDate, row.startTime].filter(Boolean).join(" · ") || "Draft row"}</div>
            </div>
            <div className="spacer" />
            <button type="button" className="btn-ghost btn mini" onClick={() => onChange(rows.filter((_, j) => j !== i))}>✕</button>
          </div>
          <div className="field-grid three schedule-line">
            <input className="input" placeholder="S.no" value={row.serial || ""} onChange={(e) => update(i, { serial: e.target.value })} />
            <input className="input" placeholder="Day" value={row.day || ""} onChange={(e) => update(i, { day: e.target.value })} />
            <input className="input" type="date" aria-label="Exact date" value={row.exactDate || ""} onChange={(e) => update(i, { exactDate: e.target.value })} />
          </div>
          <div className="field-grid three schedule-line">
            <input className="input" type="time" step="300" aria-label="Start time" value={row.startTime || ""} onChange={(e) => update(i, { startTime: e.target.value })} />
            <input className="input" type="time" step="300" aria-label="End time" value={row.endTime || ""} onChange={(e) => update(i, { endTime: e.target.value })} />
            <input className="input" placeholder="Duration" value={row.duration || ""} onChange={(e) => update(i, { duration: e.target.value })} />
          </div>
          <input className="input" placeholder="Topic" value={row.topic || ""} onChange={(e) => update(i, { topic: e.target.value })} />
          <textarea className="input" rows={3} placeholder="Topic description" value={row.description || ""} onChange={(e) => update(i, { description: e.target.value })} />
          <select className="input" value={row.speaker || ""} onChange={(e) => update(i, { speaker: e.target.value })}>
            <option value="">Select speaker or type below</option>
            {speakers.filter((s) => s.name).map((s, j) => (
              <option key={`${s.name}-${j}`} value={s.name}>{[s.name, s.title, s.org].filter(Boolean).join(" - ")}</option>
            ))}
          </select>
          <input className="input" placeholder="Custom speaker" value={row.speaker || ""} onChange={(e) => update(i, { speaker: e.target.value })} />
          <textarea className="input" rows={2} placeholder="Speaker info" value={row.speakerInfo || ""} onChange={(e) => update(i, { speakerInfo: e.target.value })} />
        </div>
      ))}
      <button type="button" className="btn-ghost btn" onClick={add}>+ Add {label}</button>
    </div>
  );
}

export function Field({ field, value, onChange, slug, details = {}, onFocus }) {
  const common = { className: "input" };
  switch (field.kind) {
    case FIELD.TOGGLE:
      return (
        <Labeled label={field.label} required={field.required} onFocus={onFocus}>
          <ToggleField value={!!value} onChange={onChange} description={field.description} />
        </Labeled>
      );
    case FIELD.TEXTAREA:
      return (
        <Labeled label={field.label} required={field.required} onFocus={onFocus}>
          <textarea {...common} rows={4} placeholder={field.placeholder}
            value={value || ""} onChange={(e) => onChange(e.target.value)} />
        </Labeled>
      );
    case FIELD.DATE:
      return (
        <Labeled label={field.label} required={field.required} onFocus={onFocus}>
          <input {...common} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} />
        </Labeled>
      );
    case FIELD.EMAIL:
      return (
        <Labeled label={field.label} required={field.required} onFocus={onFocus}>
          <input {...common} type="email" placeholder={field.placeholder}
            value={value || ""} onChange={(e) => onChange(e.target.value)} />
        </Labeled>
      );
    case FIELD.URL:
      return (
        <Labeled label={field.label} required={field.required} onFocus={onFocus}>
          <input {...common} type="url" placeholder={field.placeholder || "https://"}
            value={value || ""} onChange={(e) => onChange(e.target.value)} />
        </Labeled>
      );
    case FIELD.CHIPS:
      return (
        <Labeled label={field.label} required={field.required} onFocus={onFocus}>
          <ChipsField value={value} onChange={onChange} placeholder={field.placeholder} />
        </Labeled>
      );
    case FIELD.SPEAKERS:
      return (
        <Labeled label={field.label} required={field.required} onFocus={onFocus}>
          <SpeakersField value={value} onChange={onChange} />
        </Labeled>
      );
    case FIELD.CONTACTS:
      return (
        <Labeled label={field.label} required={field.required} onFocus={onFocus}>
          <ContactsField value={value} onChange={onChange} />
        </Labeled>
      );
    case FIELD.CUSTOM_SLIDES:
      return (
        <Labeled label={field.label} required={field.required} onFocus={onFocus}>
          <CustomSlidesField value={value} onChange={onChange} slug={slug} />
        </Labeled>
      );
    case FIELD.REGISTRATION_FIELDS:
      return (
        <Labeled label={field.label} required={field.required} onFocus={onFocus}>
          <RegistrationFieldsField value={value} onChange={onChange} />
        </Labeled>
      );
    case FIELD.HERO_IMAGE:
      return (
        <Labeled label={field.label} required={field.required} onFocus={onFocus}>
          <HeroImageField value={value} onChange={onChange} slug={slug} />
        </Labeled>
      );
    case FIELD.SCHEDULE:
      return (
        <Labeled label={field.label} required={field.required} onFocus={onFocus}>
          <ScheduleField value={value} onChange={onChange} label={field.itemLabel || "item"} speakers={details.speakers || []} />
        </Labeled>
      );
    case FIELD.TEXT:
    default:
      return (
        <Labeled label={field.label} required={field.required} onFocus={onFocus}>
          <input {...common} placeholder={field.placeholder}
            value={value || ""} onChange={(e) => onChange(e.target.value)} />
        </Labeled>
      );
  }
}
